import {
  HTMLAttributes,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { KEYBOARD } from 'commons';
import { ModalContent } from './components';
import * as S from './styles';

interface Props extends HTMLAttributes<HTMLElement> {
  modalContent: ReactElement;
}

export function Modal({ children, modalContent, ...rest }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const keyPress = useCallback(
    (e: { key: string }) => {
      if (e.key === KEYBOARD.esc && isOpen) {
        setIsOpen(false);
      }
    },
    [setIsOpen, isOpen]
  );

  useEffect(() => {
    if (!closeButtonRef) return;

    const { current } = closeButtonRef;

    if (current && isOpen) {
      current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', keyPress);

    return () => document.removeEventListener('keydown', keyPress);
  }, [keyPress]);

  function handleOpen() {
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <>
      <S.OpenButton {...rest} type="button" onClick={handleOpen}>
        {children}
      </S.OpenButton>

      {isOpen && (
        <ModalContent ref={closeButtonRef} handleClose={handleClose}>
          {modalContent}
        </ModalContent>
      )}
    </>
  );
}

export { ModalContent };
